/**
 * Batches in the Sent list — one row per batch instead of 500 indistinguishable
 * envelopes, each expanding to show the first few of its own.
 *
 * Expanding fetches that batch's first page of rows; the rest live on the batch
 * page, which is also where the row itself leads. Every envelope a batch
 * produces carries `bulkBatchId`, so a future pass can badge them in the flat
 * list too.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import EmptyState from "global/components/EmptyState/EmptyState";
import viewpaths from "global/constants/ViewPathConstants";
import { calculateRelativeTime, handleErrorMessage } from "helpers/methods";

import { batchListStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import {
  BulkBatch,
  BulkBatchRow,
  BulkRowStatus,
} from "../BulkSendTypesAndValidation";
import { recipientCountLabel, roleInitials } from "../BulkSendHelper";
import { fetchBulkBatchRows, fetchBulkBatches } from "../BulkSendService";
import { ChevronRightIcon } from "./BulkSendIcons";

const p = tokens.palette;
const t = tokens.batchList;
const s = batchListStyles;

/** Same ordering the batch page's bar uses, so the two read alike. */
const SEGMENTS: { status: BulkRowStatus; color: string }[] = [
  { status: "Sent", color: p.success },
  { status: "Sending", color: p.segmentDelivered },
  { status: "Pending", color: p.segmentQueued },
  { status: "Failed", color: p.danger },
];

const statusTone = (
  batch: BulkBatch,
): "success" | "danger" | "progress" | "neutral" => {
  if (batch.status === "CompletedWithErrors") return "danger";
  if (batch.status === "Completed") return "success";
  if (batch.status === "Queued" || batch.status === "Running")
    return "progress";
  return "neutral";
};

const statusText = (batch: BulkBatch): string => {
  const failed = batch.statusCounts?.Failed ?? 0;
  if (batch.status === "CompletedWithErrors") {
    return `${failed} failed`;
  }
  if (batch.status === "Running") return "Sending";
  if (batch.status === "Queued") return "Queued";
  return batch.status;
};

const rowTone = (row: BulkBatchRow): "success" | "danger" | "neutral" => {
  if (row.status === "Sent") return "success";
  if (row.status === "Failed") return "danger";
  return "neutral";
};

const BatchListView = () => {
  const history = useHistory();
  const [batches, setBatches] = useState<BulkBatch[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasFailed, setHasFailed] = useState<boolean>(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [children, setChildren] = useState<Record<string, BulkBatchRow[]>>({});
  const [loadingChildId, setLoadingChildId] = useState<string | null>(null);

  useEffect(() => {
    let isCurrent = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setHasFailed(false);
        const response = await fetchBulkBatches(1);
        if (!isCurrent) return;
        setBatches(response);
      } catch (error: any) {
        if (!isCurrent) return;
        setHasFailed(true);
        handleErrorMessage(error);
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    load();
    return () => {
      isCurrent = false;
    };
  }, []);

  const openBatch = useCallback(
    (batchId: string) =>
      history.push(`${viewpaths.bulkBatchViewPath}/${batchId}`),
    [history],
  );

  const toggleExpand = async (batch: BulkBatch) => {
    if (expandedId === batch.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(batch.id);
    if (children[batch.id]) return;

    try {
      setLoadingChildId(batch.id);
      const rows = await fetchBulkBatchRows(batch.id, 1);
      setChildren((current) => ({ ...current, [batch.id]: rows.slice(0, 3) }));
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoadingChildId(null);
    }
  };

  const segmentsFor = useMemo(
    () => (batch: BulkBatch) => {
      const counts = batch.statusCounts ?? {};
      const total = Math.max(batch.totalRows, 1);
      return SEGMENTS.map((segment) => ({
        ...segment,
        width: `${((counts[segment.status] ?? 0) / total) * 100}%`,
      })).filter((segment) => segment.width !== "0%");
    },
    [],
  );

  if (isLoading) {
    return (
      <>
        {[0, 1, 2].map((key) => (
          <Box key={key} sx={s.group}>
            <Box sx={{ ...s.row, cursor: "default" }}>
              <Skeleton variant="rectangular" sx={s.badge} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  variant="text"
                  sx={[s.name, { width: "38%" }] as SxProps<Theme>}
                />
                <Skeleton
                  variant="text"
                  sx={[s.meta, { width: "24%" }] as SxProps<Theme>}
                />
              </Box>
              <Skeleton variant="rectangular" sx={s.miniBar} />
            </Box>
          </Box>
        ))}
      </>
    );
  }

  if (hasFailed) {
    return (
      <EmptyState
        icon="activity"
        title="Couldn't load your batches"
        body="The request didn't come back. Try again in a moment."
      />
    );
  }

  if (batches.length === 0) {
    return (
      <EmptyState
        icon="doc"
        title="No bulk sends yet"
        body="Send one document to many people and each batch is tracked here, with its own progress and failures."
        primaryAction={{
          label: "Start a bulk send",
          onClick: () => history.push(viewpaths.bulkSendViewPath),
        }}
      />
    );
  }

  return (
    <>
      {batches.map((batch) => {
        const isExpanded = expandedId === batch.id;
        const rows = children[batch.id] ?? [];
        /*
          Only worth saying here when the status word does not already say it —
          a finished batch with failures reads "N failed" on the right, and a
          batch still sending does not.
        */
        const failedCount =
          batch.status === "CompletedWithErrors"
            ? 0
            : (batch.statusCounts?.Failed ?? 0);
        const segments = segmentsFor(batch);
        /*
          A bar of one colour filled end to end says nothing the status word
          beside it does not. It earns its width while a batch is still part
          way through, or when rows ended in different states.
        */
        const showBar = segments.length > 1 || segments[0]?.width !== "100%";
        return (
          <Box key={batch.id} sx={s.group}>
            <Box
              component="button"
              type="button"
              aria-expanded={isExpanded}
              onClick={() => toggleExpand(batch)}
              sx={s.row}
            >
              <Box component="span" sx={s.chevron(isExpanded)}>
                <ChevronRightIcon
                  size={t.chevronSize}
                  color={p.muted}
                  strokeWidth={2.5}
                />
              </Box>
              <Box component="span" sx={s.badge}>
                BATCH
              </Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                <Box component="span" sx={s.name}>
                  {batch.name}
                </Box>
                <Box component="span" sx={s.meta}>
                  {recipientCountLabel(batch.totalRows)}
                  {batch.createdOn
                    ? ` · ${calculateRelativeTime(batch.createdOn)}`
                    : ""}
                  {failedCount > 0 ? ` · ${failedCount} failed` : ""}
                </Box>
              </Box>
              {showBar && (
                <Box component="span" sx={s.miniBar}>
                  {segments.map((segment) => (
                    <Box
                      key={segment.status}
                      component="span"
                      sx={{
                        width: segment.width,
                        backgroundColor: segment.color,
                      }}
                    />
                  ))}
                </Box>
              )}
              <Box component="span" sx={s.status(statusTone(batch))}>
                {statusText(batch)}
              </Box>
            </Box>

            {isExpanded && (
              <Box sx={s.children}>
                {loadingChildId === batch.id ? (
                  [0, 1, 2].map((key) => (
                    <Box key={key} sx={s.childRow}>
                      <Skeleton
                        variant="circular"
                        sx={s.childAvatar("neutral")}
                      />
                      <Skeleton variant="text" sx={{ flex: 1 }} />
                    </Box>
                  ))
                ) : rows.length === 0 ? (
                  <Box sx={s.childRow}>
                    <Box component="span" sx={s.childWhen}>
                      No recipients to show yet.
                    </Box>
                  </Box>
                ) : (
                  rows.map((row) => {
                    const recipient = row.recipients?.[0];
                    return (
                      <Box key={`${batch.id}-${row.rowNumber}`} sx={s.childRow}>
                        <Box component="span" sx={s.childAvatar(rowTone(row))}>
                          {roleInitials(
                            recipient?.name || recipient?.email || "?",
                          )}
                        </Box>
                        <Box component="span" sx={s.childName}>
                          {recipient?.name || recipient?.email || "Unnamed"}
                        </Box>
                        <Box component="span" sx={s.childWhen}>
                          {row.sentOn
                            ? calculateRelativeTime(row.sentOn)
                            : "Not sent yet"}
                        </Box>
                        {row.status !== "Sent" && (
                          <Box
                            component="span"
                            sx={s.childStatus(rowTone(row))}
                          >
                            {row.status}
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}

                <Box
                  component="button"
                  type="button"
                  onClick={() => openBatch(batch.id)}
                  sx={s.seeAll}
                >
                  {batch.totalRows > rows.length
                    ? `See all ${batch.totalRows}`
                    : "Open batch"}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
};

export default BatchListView;
